import { LayoutSwitcher } from '@cowbell/moo';
import { ActionIcon, Tooltip } from '@cowbell/ui/core';
import {
  faArrowLeft,
  faChevronDown,
  faPenToSquare,
  faThumbtack,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faThumbtack as faThumbtackSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import styles from './AskMooHeader.module.css';

export interface AskMooHeaderProps {
  readonly agentName: string;
  readonly agentAvatar: ReactNode;
  /** Shown beside the identity while background runs are in flight. */
  readonly runBadge: ReactNode;
  readonly isFullscreen: boolean;
  readonly isFloating: boolean;
  readonly onBackToSidebar: () => void;
  readonly onOpenDrawer: () => void;
  /** Whether the drawer of agents and conversations is showing. */
  readonly isDrawerOpen: boolean;
  /** Null when no agent is selected: there is nothing to pin. */
  readonly pin: {
    readonly isDefault: boolean;
    readonly isLoading: boolean;
    readonly onToggle: () => void;
  } | null;
  readonly onNewChat: () => void;
  readonly onClose: () => void;
}

/** The Ask Moo title bar: who you are talking to (which opens the drawer of
 *  agents and conversations), and the panel's few actions. */
export function AskMooHeader({
  agentName,
  agentAvatar,
  runBadge,
  isFullscreen,
  isFloating,
  onBackToSidebar,
  onOpenDrawer,
  isDrawerOpen,
  pin,
  onNewChat,
  onClose,
}: AskMooHeaderProps) {
  const pinLabel = pin?.isDefault
    ? 'Unset default agent'
    : 'Set as default agent';

  return (
    <header
      className={styles.header}
      data-drag-handle="true"
      data-floating={isFloating}
      data-fullscreen={isFullscreen}
    >
      <div className={styles.lead}>
        {isFullscreen && (
          <Tooltip label="Back to sidebar">
            <ActionIcon
              className={styles.action}
              variant="subtle"
              color="gray"
              aria-label="Back to sidebar"
              onClick={onBackToSidebar}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </ActionIcon>
          </Tooltip>
        )}
        <button
          type="button"
          className={styles.identity}
          aria-label={`${agentName} — open drawer`}
          aria-haspopup="dialog"
          aria-expanded={isDrawerOpen}
          onClick={onOpenDrawer}
        >
          <span className={styles.identityAvatar}>{agentAvatar}</span>
          <span className={styles.identityName}>{agentName}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={styles.identityChevron}
          />
        </button>
        {runBadge}
      </div>

      <div className={styles.actions}>
        {pin && (
          <Tooltip label={pinLabel}>
            <ActionIcon
              className={styles.action}
              variant="subtle"
              color="gray"
              aria-label={pinLabel}
              data-active={pin.isDefault}
              onClick={pin.onToggle}
              loading={pin.isLoading}
            >
              <FontAwesomeIcon
                icon={pin.isDefault ? faThumbtackSolid : faThumbtack}
              />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="New chat">
          <ActionIcon
            className={styles.action}
            variant="subtle"
            color="gray"
            aria-label="New chat"
            onClick={onNewChat}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </ActionIcon>
        </Tooltip>
        <LayoutSwitcher size="md" />
        <Tooltip label="Close">
          <ActionIcon
            className={styles.action}
            variant="subtle"
            color="gray"
            aria-label="Close Ask Moo"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
        </Tooltip>
      </div>
    </header>
  );
}
